import { PaymentPeriod } from "app/components/PackageSwitcher/consts/paymentPeriod";
import type { IAddOn, IChangePackageAddonInfo } from "app/components/PackageSwitcher/types/addonsTypes";
import { numberToPrice } from "app/components/PackageSwitcher/utils/numberToPrice";
import { NUMBER_OF_MONTHS } from "app/constants/NumberOfMonths";
import type { ISwitchPackageOptions } from "app/custom-hooks/switchPackage/useSwitchPackagePopup.types";
import { translate } from "app/filters/i18nFilter";
import { lowerCaseFirst } from "app/utils/common/lowerCaseFirst";

interface IGetSwitchInfoTextParam {
    targetPackage: ISwitchPackageOptions;
    totalPrice: string;
    relativePriceWithTax: number;
    isAddonsIncluded: boolean;
    isCCWallUser: boolean;
    currencySign: string;
}

const isNextBillingDateToday = (nextBillingDate: string | null): boolean => {
    if (!nextBillingDate) {
        return false;
    }
    const billing = new Date(nextBillingDate);
    const today = new Date();
    billing.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return billing.getTime() === today.getTime();
};

export const getSwitchInfoText = ({
    relativePriceWithTax,
    isAddonsIncluded,
    targetPackage,
    totalPrice,
    currencySign,
    isCCWallUser,
}: IGetSwitchInfoTextParam) => {
    const translationPlaceHolders = {
        Price: totalPrice,
        frequency:
            targetPackage.period === PaymentPeriod.ANNUAL
                ? translate("buyer_journey.switch_package.info.payment_frequency.year")
                : translate("buyer_journey.switch_package.info.payment_frequency.month"),
        includingAddons: isAddonsIncluded ? translate("buyer_journey.switch_package.info.addons.included") : "",
        Period: targetPackage.period,
        nextBillingDate: targetPackage.nextBillingDate
            ? new Date(targetPackage.nextBillingDate).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
              })
            : "",
        relativePrice: relativePriceWithTax ? `${currencySign}${Math.round(relativePriceWithTax).toString()}` : "",
    };

    if (isCCWallUser) {
        return translate("subscription.info.nt.switch.package.description.cc.flow.v2", translationPlaceHolders);
    }

    if (isNextBillingDateToday(targetPackage.nextBillingDate)) {
        return translate("subscription.info.nt.switch.package.description.2.v3.today", translationPlaceHolders);
    }

    return translate("subscription.info.nt.switch.package.description.2.v4", translationPlaceHolders);
};

export const formatPrice = (currencySign: string, price: number, isDiscount?: boolean): string =>
    `${isDiscount ? "– " : ""}${currencySign}${numberToPrice(price, 0)}`;

const translationsPrefix = "buyer_journey.addOns";

export const getAddonsInfoByPeriod = (
    includedAddons: IAddOn[],
    period: PaymentPeriod,
    currencySign: string,
): IChangePackageAddonInfo[] => {
    const isMonthly = period === PaymentPeriod.MONTHLY;

    return includedAddons.map((addon) => {
        const monthlyPrice = isMonthly ? addon.price : Number((addon.price / NUMBER_OF_MONTHS).toFixed(2));

        const addonTypeBase = lowerCaseFirst(addon.addOnType).replace("Dynamic", "");
        const packageName = lowerCaseFirst(addon.package).replace("Dynamic", "");
        let addonName = translate(`${translationsPrefix}.${addonTypeBase}.${packageName}`);

        if (addon.countryFilterData) {
            const countryFilterDisplayName = translate(`${translationsPrefix}.countryFilter.displayName`);
            const country = translate(`common.country.${addon.countryFilterData.CountryId}`);
            addonName = `${countryFilterDisplayName}${country}`;
        }

        return {
            addonName: addonName,
            monthlyPrice,
            monthlyPriceFormatted: formatPrice(currencySign, monthlyPrice),
            annuallyPrice: addon.price,
            annuallyPriceFormatted: formatPrice(currencySign, addon.price),
        };
    });
};
